import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useCreateFolder } from '../../hooks/use-folders'
import { useIsRailDrawer } from '../../hooks/use-is-rail-drawer'
import { buildCreateDocumentBody, useCreateDocument, useDocuments } from '../../hooks/use-documents'
import { useOpenDocumentRoute, type RoutableDocument } from '../../hooks/use-open-document-route'
import { useSidebarSort } from '../../hooks/use-preferences'
import { useAppStore } from '../../stores/app-store'
import { CreateFolderModal } from '../sidebar/create-folder-modal'
import type { BrowseFilter } from '../sidebar/browse-panel'
import { CollapsedContextualSidebar, ContextualSidebarHeader } from './contextual-sidebar-chrome'
import { ContextualSidebarContent } from './contextual-sidebar-content'
import { ContextualSidebarToolbar } from './contextual-sidebar-toolbar'
import { cn } from '../../lib/utils'

export function Sidebar({ canCreate }: { canCreate: boolean }) {
  const {
    sidebarCollapsed,
    setSidebarCollapsed,
    theme,
    setMobileSidebarOpen,
    openTab,
    setActiveTab,
  } = useAppStore()
  const [search, setSearch] = useState('')
  const [showBrowse, setShowBrowse] = useState(false)
  const [folderModalOpen, setFolderModalOpen] = useState(false)
  const [browseFilter, setBrowseFilter] = useState<BrowseFilter | null>(null)
  const createFolder = useCreateFolder()
  const createDocument = useCreateDocument()
  const navigate = useNavigate()
  const drawerMode = useIsRailDrawer()
  const queryClient = useQueryClient()
  const { sortMode, sortDirection, setSortPref } = useSidebarSort()
  const openDocumentRoute = useOpenDocumentRoute()
  const isDark = theme === 'dark'

  const filterParams = browseFilter
    ? browseFilter.type === 'category'
      ? { category: browseFilter.value }
      : { tag: browseFilter.value }
    : {}
  const { data: filteredData } = useDocuments(browseFilter ? filterParams : { limit: 0 })

  const handleErrorReset = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['folders'] })
    queryClient.invalidateQueries({ queryKey: ['documents'] })
  }, [queryClient])

  const closeMobileNavigation = () => {
    if (drawerMode) setMobileSidebarOpen(false)
  }

  const handleNewDocument = async () => {
    if (!canCreate) return
    try {
      const doc = await createDocument.mutateAsync(
        buildCreateDocumentBody({
          title: 'Untitled',
          source: 'the AgentWiki workspace sidebar',
        }),
      )
      const tabId = `tab-${doc.id}`
      openTab({ id: tabId, documentId: doc.id, title: doc.title })
      setActiveTab(tabId)
      navigate(`/doc/${doc.slug}/edit`)
      closeMobileNavigation()
    } catch (error) {
      console.error('Failed to create document:', error)
    }
  }

  const handleOpenDocument = (doc: RoutableDocument) => {
    openDocumentRoute(doc)
    closeMobileNavigation()
  }

  if (!drawerMode && sidebarCollapsed) {
    return (
      <CollapsedContextualSidebar isDark={isDark} onExpand={() => setSidebarCollapsed(false)} />
    )
  }

  return (
    <aside
      aria-label="Document navigation"
      className={cn(
        'flex h-full shrink-0 flex-col border-r',
        drawerMode ? 'w-full' : 'w-[260px]',
        isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white',
      )}
    >
      <ContextualSidebarHeader
        isDark={isDark}
        isMobile={drawerMode}
        onCollapse={() => setSidebarCollapsed(true)}
        onClose={() => setMobileSidebarOpen(false)}
      />
      <ContextualSidebarToolbar
        isDark={isDark}
        search={search}
        showBrowse={showBrowse}
        createDocumentPending={createDocument.isPending}
        createFolderPending={createFolder.isPending}
        canCreate={canCreate}
        onSearchChange={setSearch}
        onNewDocument={handleNewDocument}
        onNewFolder={() => setFolderModalOpen(true)}
        onToggleBrowse={() => {
          setShowBrowse((visible) => !visible)
          if (showBrowse) setBrowseFilter(null)
        }}
      />
      <ContextualSidebarContent
        isDark={isDark}
        isMobile={drawerMode}
        search={search}
        showBrowse={showBrowse}
        browseFilter={browseFilter}
        filteredDocuments={filteredData?.data ?? []}
        sortMode={sortMode}
        sortDirection={sortDirection}
        onBrowseFilterChange={setBrowseFilter}
        onSortChange={setSortPref}
        onDocumentOpen={handleOpenDocument}
        onMobileDocumentOpen={closeMobileNavigation}
        onErrorReset={handleErrorReset}
      />
      <CreateFolderModal
        open={folderModalOpen}
        onClose={() => setFolderModalOpen(false)}
        onSubmit={async (name) => {
          try {
            await createFolder.mutateAsync({ name })
          } catch (error) {
            console.error('Failed to create folder:', error)
          }
        }}
      />
    </aside>
  )
}
